import { PORTFOLIO_PACKAGE_ID, STRATEGY_PACKAGE_ID } from "../consts/packageIds";

/**
 * Maps display role names (shown to users) to backend role names (sent to API)
 * For Portfolio/Strategy packages: "Owner" -> "Product Owner", "Agile facilitator" -> "Scrum Master"
 * For regular packages: roles remain unchanged
 */
export const mapDisplayRoleToBackend = (displayRole: string, packageId: string): string => {
  const isPortfolioOrStrategy = packageId === PORTFOLIO_PACKAGE_ID || packageId === STRATEGY_PACKAGE_ID;
  
  if (!isPortfolioOrStrategy) {
    return displayRole;
  }

  // Map Portfolio/Strategy display roles to backend roles
  switch (displayRole) {
    case "Owner":
      return "Product Owner";
    case "Agile facilitator":
      return "Scrum Master";
    default:
      return displayRole;
  }
};

/**
 * Maps display role names (shown to users) to backend role names (sent to API)
 * Using useCustomRoleMapping flag instead of package ID
 */
export const mapDisplayRoleToBackendWithCustomMapping = (displayRole: string, useCustomRoleMapping: boolean): string => {
  if (!useCustomRoleMapping) {
    return displayRole;
  }

  // Map Portfolio/Strategy display roles to backend roles
  switch (displayRole) {
    case "Owner":
      return "Product Owner";
    case "Agile facilitator":
      return "Scrum Master";
    default:
      return displayRole;
  }
};

/**
 * Maps backend role names (from API) to display role names (shown to users)
 * For Portfolio/Strategy packages: "Product Owner" -> "Owner", "Scrum Master" -> "Agile facilitator"
 * For regular packages: roles remain unchanged
 */
export const mapBackendRoleToDisplay = (backendRole: string, packageId: string): string => {
  const isPortfolioOrStrategy = packageId === PORTFOLIO_PACKAGE_ID || packageId === STRATEGY_PACKAGE_ID;
  
  if (!isPortfolioOrStrategy) {
    return backendRole;
  }

  // Map backend roles to Portfolio/Strategy display roles
  switch (backendRole) {
    case "Product Owner":
      return "Owner";
    case "Scrum Master":
      return "Agile facilitator";
    default:
      return backendRole;
  }
};

/**
 * Maps backend role names (from API) to display role names (shown to users)
 * Using useCustomRoleMapping flag instead of package ID
 */
export const mapBackendRoleToDisplayWithCustomMapping = (backendRole: string, useCustomRoleMapping: boolean): string => {
  if (!useCustomRoleMapping) {
    return backendRole;
  }

  // Map backend roles to Portfolio/Strategy display roles
  switch (backendRole) {
    case "Product Owner":
      return "Owner";
    case "Scrum Master":
      return "Agile facilitator";
    default:
      return backendRole;
  }
};

/**
 * Maps an array of display roles to backend roles
 */
export const mapDisplayRolesToBackend = (displayRoles: string[], packageId: string): string[] => {
  return displayRoles.map(role => mapDisplayRoleToBackend(role, packageId));
};

/**
 * Maps an array of display roles to backend roles using useCustomRoleMapping flag
 */
export const mapDisplayRolesToBackendWithCustomMapping = (displayRoles: string[], useCustomRoleMapping: boolean): string[] => {
  if (!displayRoles) return [];
  return displayRoles.map(role => mapDisplayRoleToBackendWithCustomMapping(role, useCustomRoleMapping));
};

/**
 * Maps an array of backend roles to display roles
 */
export const mapBackendRolesToDisplay = (backendRoles: string[], packageId: string): string[] => {
  return backendRoles.map(role => mapBackendRoleToDisplay(role, packageId));
};

/**
 * Maps an array of backend roles to display roles using useCustomRoleMapping flag
 */
export const mapBackendRolesToDisplayWithCustomMapping = (backendRoles: string[], useCustomRoleMapping: boolean): string[] => {
  if (!backendRoles) return [];
  return backendRoles.map(role => mapBackendRoleToDisplayWithCustomMapping(role, useCustomRoleMapping));
};